import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Zap, Shield, CheckCircle2, AlertTriangle, Loader2, Copy, Eye, EyeOff,
  ArrowLeft, ArrowRight, RefreshCw, Ban, Sparkles, Clock, ClipboardPaste, Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";

// Iter 111 Step 7 — Delta Exchange read-only Connect page (LIVE-WIRED).
// State machine drives itself from `GET /api/delta-connection` on mount
// and while a backfill is in progress. Copy strategy: no raw
// "API key/secret" — everything is "view-only pass".

// Delta's /v2/fills exposes ~17 months of history (Step 0 live verified).
const LOOKBACK_MONTHS = 17;
const LOOKBACK_DATE_LABEL = (() => {
  const d = new Date();
  d.setMonth(d.getMonth() - LOOKBACK_MONTHS);
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
})();

const POLL_MS = 5000;                   // user decision #2b — 5s tick

// Backend-string → user-facing copy (Step 7 error catalogue).
// Iter131 — extended with discriminated `validation_failed:<code>`
// reasons emitted by delta_sync.connect_and_validate(). Each Delta
// failure mode now has a specific, actionable message instead of the
// generic "Delta didn't accept this pass".
const ERROR_COPY = {
  revoked_or_rotated_key: {
    headline: "Your view-only pass stopped working",
    body: "Delta says the pass you connected has been deleted or its key has been rotated. This can happen if you or someone with account access removed it. Just make a new one and reconnect.",
    action: "Create a new pass",
  },
  consecutive_sync_failures: {
    headline: "Sync couldn't reach Delta for over an hour",
    body: "We tried 5 times over the last 75 minutes and each attempt failed. Delta may be down, or your internet routing might be blocking us. Please reconnect once Delta status is green.",
    action: "Try reconnecting",
  },
  // ── Discriminated validation-failed variants ──────────────────────
  invalid_api_key: {
    headline: "Delta doesn't recognise this pass",
    body: "Delta says the pass ID doesn't exist. Two common causes: (1) you accidentally pasted the secret code into the 'Pass' field (they look similar — the pass is the first value, secret is the second), or (2) the pass was deleted on Delta's side. Double-check the order, or create a fresh pass and try again.",
    action: "Re-enter pass",
  },
  signature_mismatch: {
    headline: "Your secret code doesn't match this pass",
    body: "The pass ID looks valid but the secret code doesn't match it. Copy both values fresh from Delta — the pass is the first value shown, the secret is the second. Make sure there are no hidden spaces or line breaks.",
    action: "Re-enter pass",
  },
  signature_expired: {
    headline: "Our server clock is out of sync",
    body: "Delta rejected our request because our server's clock drifted by more than 5 seconds — this isn't something you can fix on your side. Please email support@braintrades.ai and mention 'signature_expired' so we can nudge our infrastructure.",
    action: "Try again in a minute",
  },
  ip_not_whitelisted: {
    headline: "Your pass has an IP restriction",
    body: "Delta requires every API key to whitelist at least one IP, and our server isn't on this pass's list. Open the pass on Delta's API Management page and add our exact egress IPs below (one per +Add), then retry.",
    action: "Fix on Delta and retry",
  },
  unauthorized_api_access: {
    headline: "This pass is missing the Read Data permission",
    body: "Delta accepted the pass but it doesn't have 'Read Data' ticked — we can't read your wallet without it. Please delete this pass on Delta, create a fresh one with ONLY 'Read Data' ticked (leave 'Trading' unchecked), and paste the new values here.",
    action: "Re-create pass",
  },
  two_factor_required: {
    headline: "Your account needs 2FA disabled on API keys",
    body: "Delta is asking for a second factor on this pass — we can't complete that from BrainTRADES. If you can, create a new pass without 2FA required on API access. Otherwise reach out to support@braintrades.ai and we'll look at a workaround.",
    action: "Re-create pass",
  },
  // ── Legacy generic fallback (kept for any un-migrated flows) ──────
  validation_failed: {
    headline: "Delta didn't accept this pass",
    body: "The pass or secret code you entered wasn't recognised by Delta. Double-check you copied them fully (no trailing spaces) and try again.",
    action: "Re-enter pass",
  },
  attestation_missing: {
    headline: "One more confirmation needed",
    body: "Please tick the box confirming your pass is view-only (no trading permission) before we connect.",
    action: "Back to form",
  },
  default: {
    headline: "Something went wrong on our side",
    body: "We couldn't complete this step. It's not on you — please try again in a moment.",
    action: "Try again",
  },
};

function classifyReason(reason) {
  const s = (reason || "").toLowerCase();
  if (s.includes("revoked") || s.includes("rotated")) return "revoked_or_rotated_key";
  if (s.includes("consecutive")) return "consecutive_sync_failures";
  if (s.includes("attestation")) return "attestation_missing";
  // iter131 — new discriminated `validation_failed:<code>` shape.
  // Try the specific code first, fall back to generic if unmapped.
  const m = s.match(/validation_failed:([a-z0-9_]+)/);
  if (m && ERROR_COPY[m[1]]) return m[1];
  if (s.includes("validation") || s.includes("401") || s.includes("invalid")) return "validation_failed";
  return "default";
}

// A genuine platform/unexpected error carries a server-logged reference
// (`platform_error:<REF>`). Surface it so the user can quote it to support.
function extractErrorRef(reason) {
  const m = String(reason || "").match(/platform_error:([A-Za-z0-9]+)/);
  return m ? m[1].toUpperCase() : null;
}

// iter131 — strong paste sanitiser. `.trim()` misses non-breaking
// spaces (U+00A0), zero-width chars (U+200B–U+200D, U+FEFF), CRs
// (Windows clipboards), and any embedded whitespace inside the token.
// Delta keys/secrets are single alphanumeric-ish tokens — we strip
// ALL Unicode whitespace + common zero-width chars anywhere in the
// string, not just at the ends.
function _sanitizePass(v) {
  if (!v) return "";
  return String(v).replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, "");
}

// ─── Egress-IP panel (2026-09-16, 3Commas-style live IP allowlist UX) ──
// Live-pulls BrainTRADES' outbound IPs from GET /api/integrations/delta/
// egress-ips (server config, never a stale doc) and renders them in a
// monospace box with one-click copy. Reused by the setup panel and the
// IP-restriction error state so the same data + copy widget appears in
// both places — the user never has to email support for the IPs.
// ─── Broker registry ─────────────────────────────────────────────────
// The Auto-sync setup UI is broker-parameterised: every broker-specific
// string (name, API-key page URL, IP-field label, egress endpoint) lives
// here so adding a second broker later is a config entry — not a rewrite
// of EgressIpPanel / HowToCard.
const BROKERS = {
  delta: {
    id: "delta",
    name: "Delta Exchange",
    shortName: "Delta",
    // NOTE: www.india.delta.exchange is NXDOMAIN; india.delta.exchange
    // 301s to www.delta.exchange, so we point straight at the canonical
    // dashboard. The API host (api.india.delta.exchange) is separate.
    apiKeyPageUrl: "https://www.delta.exchange/app/account/manageapikeys",
    ipFieldLabel: "Trusted IPs to Whitelist",
    apiManagementPageName: "API Management",
    egressEndpoint: "/integrations/delta/egress-ips",
  },
};
const DEFAULT_BROKER = BROKERS.delta;

function EgressIpPanel({ variant = "setup", broker = DEFAULT_BROKER }) {
  const [ips, setIps] = useState([]);
  const [currentIp, setCurrentIp] = useState(null);
  const [inSet, setInSet] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedCurrent, setCopiedCurrent] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(broker.egressEndpoint)
      .then(({ data }) => {
        if (!alive) return;
        setIps(data?.ips || []);
        setCurrentIp(data?.current_egress_ip || null);
        setInSet(typeof data?.current_egress_in_set === "boolean" ? data.current_egress_in_set : null);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [broker.egressEndpoint]);

  const copyText = async (text, done) => {
    try {
      await navigator.clipboard.writeText(text);
      done();
    } catch { /* clipboard blocked — value is still selectable */ }
  };
  const copyOne = (ip, idx) => copyText(ip, () => {
    setCopiedIdx(idx); setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1800);
  });
  const copyAll = () => copyText(ips.join(","), () => {
    setCopiedAll(true); setTimeout(() => setCopiedAll(false), 1800);
  });
  const copyCurrent = () => currentIp && copyText(currentIp, () => {
    setCopiedCurrent(true); setTimeout(() => setCopiedCurrent(false), 1800);
  });

  if (loaded && ips.length === 0) return null;
  return (
    <div
      data-testid="delta-egress-ip-panel"
      className="rounded-md border border-[var(--bt-border)] bg-[var(--bt-surface-2)] p-4 space-y-3"
    >
      {/* TOP: which IP we're reaching Delta from RIGHT NOW — the one that
          matters most for a first connect. Alerts if it's not in our
          published set (list drifted / infra change). */}
      {currentIp && (
        <div
          data-testid="delta-current-egress-top"
          className="rounded-md border px-3 py-2.5"
          style={inSet === false
            ? { borderColor: "color-mix(in srgb, var(--bt-warn) 55%, var(--bt-border))", background: "color-mix(in srgb, var(--bt-warn) 10%, transparent)" }
            : { borderColor: "var(--bt-border)", background: "var(--bt-bg)" }}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-[var(--bt-text-muted)]">
              One of our egress IPs (right now:{" "}
              <span className="font-mono font-semibold text-[var(--bt-text)]" data-testid="delta-current-egress-ip">{currentIp}</span>).
              Our servers rotate across all the IPs below per request, so whitelist them all.
            </p>
            <Button
              type="button" variant="outline" size="sm" onClick={copyCurrent}
              data-testid="delta-current-egress-copy-btn"
              className="border-[var(--bt-border)] shrink-0 h-7 px-2.5"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> {copiedCurrent ? "Copied" : "Copy"}
            </Button>
          </div>
          {inSet === false && (
            <p className="text-[11px] mt-1.5 flex items-start gap-1.5" style={{ color: "var(--bt-warn)" }} data-testid="delta-current-egress-alert">
              <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
              This IP isn&apos;t in the list below yet — make sure you whitelist it too (it&apos;s safe to add). If your connection still fails after adding all IPs, contact support with this IP.
            </p>
          )}
        </div>
      )}

      {/* Delta REQUIRES at least one Trusted IP to create an API key. */}
      <div
        className="rounded-md border px-3 py-2.5"
        style={{ borderColor: "color-mix(in srgb, var(--bt-accent) 45%, var(--bt-border))", background: "color-mix(in srgb, var(--bt-accent) 8%, transparent)" }}
      >
        <p className="font-semibold text-sm flex items-center gap-1.5" style={{ color: "var(--bt-accent)" }} data-testid="delta-egress-required">
          <Shield className="w-4 h-4" /> Whitelist these {ips.length} IPs — {broker.name} requires it
        </p>
        <p className="text-xs text-[var(--bt-text-muted)] mt-1">
          {broker.name}&apos;s &quot;Create API Key&quot; button stays <b>disabled until you add at least one
          IP to &quot;{broker.ipFieldLabel}&quot;</b> — there&apos;s no &quot;unrestricted&quot; option. Add all {ips.length} of our
          egress IPs so the key keeps working no matter which server we sync from.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-[var(--bt-text-muted)]" data-testid="delta-egress-whitelist-instructions">
            Fastest: click <b>Copy all IPs</b> and paste the whole list into
            &quot;{broker.ipFieldLabel}&quot; in one go ({broker.name} accepts a comma-separated
            list, up to 50 IPs). Or copy them one at a time and +Add each.
          </p>
          <Button
            type="button" variant="outline" size="sm" onClick={copyAll}
            data-testid="delta-egress-copy-all-btn"
            className="border-[var(--bt-border)] shrink-0 h-8 px-2.5"
          >
            {copiedAll ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
            {copiedAll ? "Copied all" : "Copy all IPs"}
          </Button>
        </div>
        <ul className="space-y-1.5" data-testid="delta-egress-ip-list">
          {ips.map((ip, idx) => (
            <li key={ip} className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs bg-[var(--bt-bg)] border border-[var(--bt-border)] rounded-md px-2.5 py-1.5">
                {ip}
              </code>
              <Button
                type="button" variant="outline" size="sm" onClick={() => copyOne(ip, idx)}
                data-testid={`delta-egress-copy-btn-${idx}`}
                className="border-[var(--bt-border)] shrink-0 h-8 px-2.5"
              >
                <Copy className="w-3.5 h-3.5 mr-1" /> {copiedIdx === idx ? "Copied" : "Copy"}
              </Button>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-[var(--bt-text-muted)]">
          Add all {ips.length} — our backend may egress from any of them across restarts.
          If your ISP/IP changes later, you can edit the key&apos;s IPs on {broker.name}&apos;s {broker.apiManagementPageName} page anytime.
        </p>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────

export default function DeltaConnect() {
  const nav = useNavigate();

  // `phase` is what we render; `conn` is the latest server snapshot.
  //   phase: "loading" | "intro" | "how_to" | "paste" | "verifying"
  //        | "backfilling" | "active" | "error"
  const [phase, setPhase] = useState("loading");
  const [conn, setConn] = useState(null);

  // Fetch current connection state on mount + choose a starting phase.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/delta-connection");
      setConn(data);
      return data;
    } catch {
      setConn(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const c = await refresh();
      if (!alive) return;
      setPhase(deriveInitialPhase(c));
    })();
    return () => { alive = false; };
  }, [refresh]);

  // Poll while a backfill is in progress. Stops as soon as the state
  // transitions to "completed"/"error"/"rate_limited" or the user
  // navigates away.
  useEffect(() => {
    if (phase !== "backfilling") return;
    const id = setInterval(async () => {
      const c = await refresh();
      if (!c) return;
      const bs = c.backfill_state;
      if (bs === "completed") setPhase("active");
      else if (bs === "error") setPhase("error");
      // "rate_limited" is a transient — the backend will auto-resume;
      // keep polling and stay on the backfill screen (see banner below).
    }, POLL_MS);
    return () => clearInterval(id);
  }, [phase, refresh]);

  const setPhaseFrom = useCallback((c) => {
    setConn(c);
    setPhase(derivePhase(c));
  }, []);

  return (
    <div data-testid="delta-connect-page" className="space-y-6 max-w-3xl">
      <Header onBack={() => nav("/trade-doctor")} />

      {phase === "loading"      && <LoadingCard />}
      {phase === "intro"        && <IntroCard   onNext={() => setPhase("how_to")} />}
      {phase === "how_to"       && <HowToCard   onBack={() => setPhase("intro")} onNext={() => setPhase("paste")} broker={DEFAULT_BROKER} />}
      {phase === "paste"        && <PasteCard   onBack={() => setPhase("how_to")}
                                                onVerifying={() => setPhase("verifying")}
                                                onVerified={(c) => setPhaseFrom(c)}
                                                onError={(c) => setPhaseFrom(c)} />}
      {phase === "verifying"    && <VerifyingCard />}
      {phase === "backfilling"  && <BackfillCard conn={conn} />}
      {phase === "active"       && <ActiveCard   conn={conn} onDisconnected={() => { setConn(null); setPhase("intro"); }} />}
      {phase === "error"        && <ErrorCard    conn={conn} onRetry={() => setPhase("paste")} />}
    </div>
  );
}

function deriveInitialPhase(c) {
  if (!c || c.status === "not_connected") return "intro";
  return derivePhase(c);
}

function derivePhase(c) {
  if (!c) return "intro";
  if (c.status === "error") return "error";
  if (c.status === "pending" || c.status === "validated") {
    const bs = c.backfill_state;
    if (bs === "in_progress" || bs === "rate_limited") return "backfilling";
    if (bs === "not_started") return "backfilling";     // just kicked off
    return "backfilling";
  }
  if (c.status === "active") {
    if (c.backfill_state === "in_progress") return "backfilling";
    return "active";
  }
  return "intro";
}

// ─── Chrome ──────────────────────────────────────────────────────────

function Header({ onBack }) {
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onBack}
        data-testid="delta-connect-back"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to Trade Doctor
      </button>
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "color-mix(in srgb, var(--bt-accent) 12%, var(--bt-surface))" }}
        >
          <Zap className="w-5 h-5" style={{ color: "var(--bt-accent)" }} />
        </div>
        <div className="flex-1">
          <p className="bt-label" style={{ color: "var(--bt-accent)" }}>New · Beta</p>
          <h1 className="font-heading text-2xl sm:text-3xl">Auto-sync from Delta</h1>
          <p className="text-sm text-[var(--bt-text-muted)] mt-1 max-w-xl">
            Skip the CSV export. Give BrainTRADES a view-only pass and your trades appear here — same day, every day.
          </p>
        </div>
      </div>
    </div>
  );
}

function LoadingCard() {
  return (
    <section
      data-testid="delta-connect-loading-card"
      className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-8 flex items-center justify-center gap-3"
    >
      <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--bt-accent)" }} />
      <span className="text-sm text-[var(--bt-text-muted)]">Checking your connection…</span>
    </section>
  );
}

// ─── Screen 1 — Intro ─────────────────────────────────────────────────

function IntroCard({ onNext }) {
  return (
    <section
      data-testid="delta-connect-intro-card"
      className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Bullet icon={<CheckCircle2 />} title="No CSV exports" body="New trades stream in live, with an automatic safety-net sync as backup." />
        <Bullet icon={<Shield />}       title="View-only, always" body="We literally cannot place trades — even if we wanted to." />
        <Bullet icon={<Clock />}        title={`Syncs back ~${LOOKBACK_MONTHS} months`} body={`Delta gives us history from ~${LOOKBACK_DATE_LABEL}.`} />
      </div>

      <div
        data-testid="delta-connect-lookback-notice"
        className="rounded-md border px-4 py-3 text-sm"
        style={{
          background: "color-mix(in srgb, var(--bt-accent) 6%, var(--bt-surface-2))",
          borderColor: "color-mix(in srgb, var(--bt-accent) 35%, var(--bt-border))",
        }}
      >
        <p className="bt-label mb-1" style={{ color: "var(--bt-accent)" }}>
          Before you connect — how far back we can see
        </p>
        <p className="text-[var(--bt-text)] leading-relaxed">
          Delta only lets us pull the last <b>~{LOOKBACK_MONTHS} months</b> of your trades
          (roughly since <b>{LOOKBACK_DATE_LABEL}</b>). If you have older trades you want
          in your journal, keep uploading those CSVs — we won&apos;t double-count them.
        </p>
      </div>

      <div className="rounded-md border border-[var(--bt-border)] px-4 py-3 space-y-2 text-sm">
        <p className="bt-label" style={{ color: "var(--bt-text-muted)" }}>How this works</p>
        <ul className="space-y-1.5 text-[var(--bt-text)]">
          <TinyRow label="You create a view-only pass on Delta (takes 60 seconds)." />
          <TinyRow label="You paste it here. We encrypt it before storing — nobody at BrainTRADES can read it." />
          <TinyRow label={`We fetch your ~${LOOKBACK_MONTHS} months of trade history, then keep it live — new fills stream in within seconds over a secure websocket.`} />
          <TinyRow label="You can revoke the pass from Delta any time. We'll notice and stop syncing." />
        </ul>
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <Link
          to="/trade-doctor"
          className="text-sm text-[var(--bt-text-muted)] hover:text-[var(--bt-text)]"
          data-testid="delta-connect-intro-cancel"
        >
          Maybe later
        </Link>
        <Button
          data-testid="delta-connect-intro-next"
          onClick={onNext}
          className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-semibold"
        >
          Start — takes 2 minutes <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </section>
  );
}

function Bullet({ icon, title, body }) {
  return (
    <div
      className="rounded-md border border-[var(--bt-border)] px-4 py-3 space-y-1.5"
      style={{ background: "var(--bt-surface-2)" }}
    >
      <div className="flex items-center gap-2">
        <span className="w-4 h-4 shrink-0" style={{ color: "var(--bt-accent)" }}>
          {React.cloneElement(icon, { className: "w-4 h-4" })}
        </span>
        <p className="text-sm font-semibold">{title}</p>
      </div>
      <p className="text-xs text-[var(--bt-text-muted)] leading-relaxed">{body}</p>
    </div>
  );
}

function TinyRow({ label }) {
  return (
    <li className="flex items-start gap-2">
      <span
        className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
        style={{ background: "var(--bt-accent)" }}
      />
      <span className="text-sm leading-relaxed">{label}</span>
    </li>
  );
}

// ─── Screen 2 — How to create the Delta view-only pass ──────────────

function HowToCard({ onBack, onNext, broker = DEFAULT_BROKER }) {
  const [linkCopied, setLinkCopied] = useState(false);
  const [showLinkFallback, setShowLinkFallback] = useState(false);
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(broker.apiKeyPageUrl);
      setLinkCopied(true); setTimeout(() => setLinkCopied(false), 1800);
    } catch { /* clipboard blocked — URL text is still selectable */ }
  };
  // Single-click open. window.open on a user gesture is not popup-blocked
  // in the normal case (tab opens directly). If it returns null the
  // browser DID block it, so we reveal the copyable link as a safety net.
  const openBroker = () => {
    const w = window.open(broker.apiKeyPageUrl, "_blank");
    if (w) { try { w.opener = null; } catch { /* noop */ } }
    else { setShowLinkFallback(true); }
  };
  return (
    <section
      data-testid="delta-connect-howto-card"
      className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-5"
    >
      <div>
        <p className="bt-label">Step 1 of 2</p>
        <h2 className="font-heading text-xl mt-0.5">Create a view-only pass on {broker.shortName}</h2>
        <p className="text-sm text-[var(--bt-text-muted)] mt-1">
          Follow these 6 steps in the {broker.shortName} app. It takes about 60 seconds.
        </p>
      </div>

      <ol className="space-y-3">
        <HowStep n={1} title={`Open ${broker.shortName} → Account → API Keys`} body="Log in, tap the profile icon (top-right), then 'Manage Account → API Keys'." />
        <HowStep n={2} title="Tap 'Create API Key'" body="You'll see a form with a name field and permission checkboxes." />
        <HowStep n={3} title="Name it 'BrainTRADES sync'" body="Any name works — this just helps you remember which pass belongs to BrainTRADES if you make more later." />
        <HowStep n={4} emphasize
          title="Tick ONLY 'Read Data' — leave 'Trading' UNCHECKED"
          body="This is the whole reason it's called a view-only pass. If 'Trading' is checked, we refuse to connect on purpose (even if you paste it here)." />
        <HowStep n={5} emphasize
          title={`Add our IPs to '${broker.ipFieldLabel}' — REQUIRED`}
          body={`${broker.name} won't let you create the key (the Create button stays disabled) until you add at least one IP. Use 'Copy all IPs' in the box below and paste the whole comma-separated list into the field (${broker.name} accepts up to 50).`} />
        <HowStep n={6} title={`Copy BOTH values ${broker.shortName} shows you`} body={`${broker.shortName} will show a long pass and a secret code. Copy both — you'll paste them on the next screen.`} />
      </ol>

      <div
        className="rounded-md border px-4 py-3 flex items-start gap-3 text-sm"
        style={{
          borderColor: "color-mix(in srgb, var(--bt-warn) 40%, var(--bt-border))",
          background: "color-mix(in srgb, var(--bt-warn) 6%, var(--bt-surface-2))",
        }}
      >
        <Ban className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--bt-warn)" }} />
        <div className="space-y-1">
          <p className="font-semibold">If &apos;Trading&apos; is checked, DO NOT paste it here.</p>
          <p className="text-[var(--bt-text-muted)] text-xs">
            Our backend refuses non-read operations by design, but you shouldn&apos;t rely on
            our safety net — just don&apos;t create a trading key in the first place.
          </p>
        </div>
      </div>

      <EgressIpPanel variant="setup" broker={broker} />

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <Button variant="outline" onClick={onBack} data-testid="delta-connect-howto-back"
          className="border-[var(--bt-border)]">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex items-center gap-1.5">
            <Button variant="outline" onClick={openBroker} data-testid="delta-connect-open-delta"
              className="border-[var(--bt-border)]">
              Open {broker.shortName} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button variant="outline" size="icon" onClick={copyUrl}
              data-testid="delta-connect-copy-delta-url"
              title={`Copy ${broker.shortName} link`} aria-label={`Copy ${broker.shortName} link`}
              className="border-[var(--bt-border)] shrink-0">
              {linkCopied
                ? <CheckCircle2 className="w-4 h-4" style={{ color: "var(--bt-accent)" }} />
                : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <Button onClick={onNext} data-testid="delta-connect-howto-next"
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-semibold">
            I have my pass — continue <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>

      {/* Fallback shown ONLY when the tab-open was blocked (window.open
          returned null). Lets the user copy the exact URL to open manually. */}
      {showLinkFallback && (
        <div
          data-testid="delta-connect-open-delta-fallback"
          className="rounded-md border px-3 py-2.5 space-y-1.5"
          style={{ borderColor: "color-mix(in srgb, var(--bt-warn) 40%, var(--bt-border))", background: "color-mix(in srgb, var(--bt-warn) 6%, var(--bt-surface-2))" }}
        >
          <p className="text-xs text-[var(--bt-text-muted)]">
            Couldn&apos;t open a new tab — a popup blocker or extension may have stopped it.
            Copy this link and open it manually:
          </p>
          <div className="flex items-center gap-2">
            <code
              data-testid="delta-connect-delta-url"
              className="flex-1 font-mono text-xs bg-[var(--bt-bg)] border border-[var(--bt-border)] rounded-md px-2.5 py-1.5 overflow-x-auto whitespace-nowrap select-all"
            >{broker.apiKeyPageUrl}</code>
            <Button
              type="button" variant="outline" size="sm" onClick={copyUrl}
              className="border-[var(--bt-border)] shrink-0 h-8 px-2.5"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> {linkCopied ? "Copied" : "Copy link"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function HowStep({ n, title, body, emphasize }) {
  return (
    <li
      data-testid={`delta-connect-howstep-${n}`}
      className="flex items-start gap-3 rounded-md border px-4 py-3"
      style={{
        borderColor: emphasize
          ? "color-mix(in srgb, var(--bt-accent) 50%, var(--bt-border))"
          : "var(--bt-border)",
        background: emphasize
          ? "color-mix(in srgb, var(--bt-accent) 6%, var(--bt-surface-2))"
          : "var(--bt-surface-2)",
      }}
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
        style={{
          background: emphasize ? "var(--bt-accent)" : "var(--bt-surface)",
          color: emphasize ? "#000" : "var(--bt-text)",
        }}
      >
        {n}
      </span>
      <div className="space-y-0.5">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-[var(--bt-text-muted)] leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

// ─── Screen 3 — Paste ─────────────────────────────────────────────────

function PasteCard({ onBack, onVerifying, onVerified, onError }) {
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [attested, setAttested] = useState(false);
  const [pasteHint, setPasteHint] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);   // {ok, is_ip_restriction, current_egress_ip, delta_code}

  const canTest = _sanitizePass(apiKey).length >= 8 && _sanitizePass(apiSecret).length >= 8 && !testing && !busy;

  const testConnection = async () => {
    if (!canTest) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/integrations/delta/test-connection", {
        api_key: _sanitizePass(apiKey),
        api_secret: _sanitizePass(apiSecret),
      });
      setTestResult(data);
      if (data.ok) toast.success("Connection works — your view-only pass is valid.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't reach Delta — try again.";
      setTestResult({ ok: false, error_reason: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };
  const focusedRef = useRef(false);

  // Auto-paste detection on tab return (per requirement 3).
  useEffect(() => {
    const check = async () => {
      if (document.visibilityState !== "visible") return;
      if (!focusedRef.current) return;
      if (!navigator.clipboard || !navigator.clipboard.readText) return;
      try {
        const text = (await navigator.clipboard.readText()).trim();
        if (!text || /\s/.test(text)) return;
        if (text.length >= 24 && text.length <= 200) {
          setPasteHint((prev) => ({
            ...(prev || {}),
            [apiKey ? "secret" : "key"]: text,
          }));
        }
      } catch { /* permission denied — silent */ }
    };
    const onFocus = () => { focusedRef.current = true; check(); };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", onFocus);
    };
  }, [apiKey]);

  const applyPasteHint = (field) => {
    if (!pasteHint || !pasteHint[field]) return;
    if (field === "key") setApiKey(pasteHint.key);
    else setApiSecret(pasteHint.secret);
    setPasteHint((prev) => ({ ...(prev || {}), [field]: null }));
    toast.success("Pasted from clipboard.");
  };

  const canVerify = _sanitizePass(apiKey).length >= 8 && _sanitizePass(apiSecret).length >= 8 && attested && !busy;

  const submit = async () => {
    if (!canVerify) return;
    setBusy(true);
    onVerifying();                                   // parent → "verifying"
    try {
      await api.post("/delta-connect", {
        api_key: _sanitizePass(apiKey),
        api_secret: _sanitizePass(apiSecret),
        attestation_read_only: true,
      });
      // Backend kicks off backfill in a background task; the current
      // connection doc will now be status=validated or =active with
      // backfill_state=in_progress. Fetch and hand back to parent.
      const { data } = await api.get("/delta-connection");
      onVerified(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail || "Couldn't connect — please try again.");
      // Fetch the latest state so ErrorCard has the accurate reason.
      try {
        const { data } = await api.get("/delta-connection");
        onError(data);
      } catch { onError({ status: "error", error_reason: detail }); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="delta-connect-paste-card"
      className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-5"
    >
      <div>
        <p className="bt-label">Step 2 of 2</p>
        <h2 className="font-heading text-xl mt-0.5">Paste your view-only pass</h2>
        <p className="text-sm text-[var(--bt-text-muted)] mt-1">
          Both values Delta showed you. We encrypt them the moment you tap Verify.
        </p>
      </div>

      <PasteField
        testid="delta-connect-key-input"
        label="View-only pass"
        hint="Delta labels this 'API Key'. It's the shorter of the two values."
        value={apiKey}
        onChange={setApiKey}
        onPasteHint={pasteHint?.key ? () => applyPasteHint("key") : null}
      />

      <PasteField
        testid="delta-connect-secret-input"
        label="Secret code"
        hint="Delta labels this 'API Secret'. Longer of the two. Shown once — copy it now."
        value={apiSecret}
        onChange={setApiSecret}
        onPasteHint={pasteHint?.secret ? () => applyPasteHint("secret") : null}
        sensitive
        show={showSecret}
        onToggleShow={() => setShowSecret((v) => !v)}
      />

      <label
        data-testid="delta-connect-attestation-label"
        className="flex items-start gap-3 rounded-md border px-4 py-3 cursor-pointer select-none"
        style={{
          borderColor: attested
            ? "color-mix(in srgb, var(--bt-accent) 55%, var(--bt-border))"
            : "var(--bt-border)",
          background: attested
            ? "color-mix(in srgb, var(--bt-accent) 6%, var(--bt-surface-2))"
            : "var(--bt-surface-2)",
        }}
      >
        <input
          type="checkbox"
          data-testid="delta-connect-attestation-checkbox"
          checked={attested}
          onChange={(e) => setAttested(e.target.checked)}
          className="mt-1 accent-[var(--bt-accent)]"
        />
        <div className="text-sm space-y-1">
          <p className="font-semibold">I confirm this pass is view-only.</p>
          <p className="text-xs text-[var(--bt-text-muted)] leading-relaxed">
            When I created it on Delta, only <b>Read Data</b> was ticked. <b>Trading</b>{" "}
            was left unchecked. This is a hard requirement — we refuse to connect otherwise.
          </p>
        </div>
      </label>

      {/* iter133 6.1 item 3 — Guardrail transparency. Same honest
          copy as /admin/health, but for end users. The read-only
          guarantee is enforced by our code, not by Delta's API
          (Delta India doesn't publish per-key scopes anywhere) —
          users deserve to know what actually protects them. */}
      <div
        data-testid="delta-connect-guardrail-callout"
        className="rounded-md border px-4 py-3 space-y-2"
        style={{
          borderColor: "var(--bt-border)",
          background: "var(--bt-surface-2)",
        }}
      >
        <div className="flex items-start gap-2">
          <Lock
            className="w-3.5 h-3.5 mt-1 shrink-0"
            style={{ color: "var(--bt-accent)" }}
          />
          <p className="text-xs font-semibold uppercase tracking-wider">
            How we enforce read-only
          </p>
        </div>
        <p
          data-testid="delta-connect-guardrail-body"
          className="text-xs text-[var(--bt-text-muted)] leading-relaxed"
        >
          Delta India doesn't publish per-key permissions we can check
          from our end. What actually protects you is our own code:
          every Delta request goes through a guard that only lets{" "}
          <b>six specific GET paths</b> through (balances, fills,
          products, positions, transactions) — any write attempt raises
          a security error before a socket is even opened. Our
          WebSocket outbound frames are guarded the same way. Your
          checkbox above is the third layer. All three have to pass;
          none of them lets a trade slip through.
        </p>
        <p className="text-xs text-[var(--bt-text-muted)]">
          If you're technical, admins can view the live guardrail state
          at{" "}
          <code
            data-testid="delta-connect-guardrail-admin-pointer"
            className="font-mono"
          >
            /admin/health
          </code>
          .
        </p>
      </div>

      {/* Fix — Test Connection result: instant pass/fail before committing. */}
      {testResult && (
        <div
          data-testid="delta-connect-test-result"
          className="rounded-md border px-4 py-3 space-y-2 text-sm"
          style={{
            borderColor: testResult.ok
              ? "color-mix(in srgb, var(--bt-accent) 45%, var(--bt-border))"
              : "color-mix(in srgb, var(--bt-loss) 45%, var(--bt-border))",
            background: testResult.ok
              ? "color-mix(in srgb, var(--bt-accent) 6%, var(--bt-surface))"
              : "color-mix(in srgb, var(--bt-loss) 6%, var(--bt-surface))",
          }}
        >
          {testResult.ok ? (
            <p className="font-semibold flex items-center gap-2" style={{ color: "var(--bt-accent)" }}>
              <CheckCircle2 className="w-4 h-4" /> Connection works — your view-only pass is valid.
            </p>
          ) : (
            <>
              <p className="font-semibold flex items-center gap-2" style={{ color: "var(--bt-loss)" }}>
                <AlertTriangle className="w-4 h-4" />
                {testResult.is_ip_restriction
                  ? "Blocked by an IP restriction on your pass"
                  : (ERROR_COPY[classifyReason(testResult.error_reason)]?.headline || "Connection failed")}
              </p>
              {testResult.is_ip_restriction && <EgressIpPanel variant="error" broker={DEFAULT_BROKER} />}
              {testResult.current_egress_ip && (
                <p className="text-[11px] text-[var(--bt-text-muted)]" data-testid="delta-connect-test-current-egress">
                  We&apos;re currently reaching Delta from <span className="font-mono">{testResult.current_egress_ip}</span>.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <Button variant="outline" onClick={onBack} data-testid="delta-connect-paste-back"
          className="border-[var(--bt-border)]" disabled={busy}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            data-testid="delta-connect-test-btn"
            onClick={testConnection}
            disabled={!canTest}
            className="border-[var(--bt-border)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Zap className="w-4 h-4 mr-1.5" />}
            Test connection
          </Button>
          <Button
            data-testid="delta-connect-verify-btn"
            onClick={submit}
            disabled={!canVerify}
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Lock className="w-4 h-4 mr-1.5" />}
            Verify &amp; connect
          </Button>
        </div>
      </div>
    </section>
  );
}

function PasteField({ testid, label, hint, value, onChange, sensitive, show, onToggleShow, onPasteHint }) {
  const type = sensitive && !show ? "password" : "text";
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold">{label}</label>
        {onPasteHint && (
          <button
            type="button"
            onClick={onPasteHint}
            data-testid={`${testid}-paste-detect`}
            className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
            style={{
              background: "color-mix(in srgb, var(--bt-accent) 14%, var(--bt-surface))",
              color: "var(--bt-accent)",
            }}
          >
            <ClipboardPaste className="w-3 h-3" /> Paste from clipboard
          </button>
        )}
      </div>
      <div className="relative">
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          placeholder={sensitive ? "•••••••••••••••••••" : "Paste here"}
          className="w-full font-mono text-sm rounded-md border px-3 py-2 pr-10 focus:outline-none focus:ring-2 transition"
          style={{
            borderColor: "var(--bt-border)",
            background: "var(--bt-surface-2)",
            color: "var(--bt-text)",
          }}
        />
        {sensitive && (
          <button
            type="button"
            onClick={onToggleShow}
            data-testid={`${testid}-toggle`}
            aria-label={show ? "Hide secret" : "Show secret"}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[var(--bt-surface)] transition-colors"
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      <p className="text-xs text-[var(--bt-text-muted)]">{hint}</p>
    </div>
  );
}

// ─── Screen 4 — Verifying (short-lived) ──────────────────────────────

function VerifyingCard() {
  return (
    <section
      data-testid="delta-connect-verifying-card"
      className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-8 flex flex-col items-center text-center gap-4"
    >
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center"
        style={{ background: "color-mix(in srgb, var(--bt-accent) 14%, var(--bt-surface))" }}
      >
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: "var(--bt-accent)" }} />
      </div>
      <div className="space-y-1 max-w-md">
        <h2 className="font-heading text-xl">Checking your pass with Delta…</h2>
        <p className="text-sm text-[var(--bt-text-muted)]">
          One quick handshake — we&apos;re asking Delta to confirm this pass is real and
          view-only. Usually done in under 3 seconds.
        </p>
      </div>
    </section>
  );
}

// ─── Screen 5 — Backfilling (honest progress bar, 5s polling) ────────

function BackfillCard({ conn }) {
  const rateLimited = conn?.backfill_state === "rate_limited";
  const pages = conn?.backfill_stats?.pages ?? 0;
  const fills = conn?.fills_fetched ?? 0;
  const trades = conn?.backfill_stats?.trades_created ?? 0;
  // Honest sizing per user decision #1c: the backend probes total pages
  // after page 1 and stores `estimated_total_pages`. We divide against
  // that and cap the visible bar at 99% until backfill_state=completed
  // so we never render "100% done" while pages are still coming.
  const total = Math.max(conn?.estimated_total_pages || 20, pages + 1);
  const pct = Math.min(99, Math.max(0, Math.round((pages / total) * 100)));

  return (
    <section
      data-testid="delta-connect-backfill-card"
      className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-5"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "color-mix(in srgb, var(--bt-accent) 14%, var(--bt-surface))" }}
        >
          <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--bt-accent)" }} />
        </div>
        <div className="flex-1 space-y-1">
          <p className="bt-label" style={{ color: "var(--bt-accent)" }}>Syncing now</p>
          <h2 className="font-heading text-xl">Pulling your Delta history…</h2>
          <p className="text-sm text-[var(--bt-text-muted)]">
            This takes 1–3 minutes for most accounts. You can leave this page —
            it&apos;ll keep running in the background and pop back to Trade Doctor when done.
          </p>
        </div>
      </div>

      <div
        data-testid="delta-connect-backfill-progress"
        className="rounded-md border px-4 py-4 space-y-3"
        style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface-2)" }}
      >
        <div className="flex items-center justify-between text-xs text-[var(--bt-text-muted)]">
          <span>Progress</span>
          <span data-testid="delta-connect-backfill-pct">{pct}%</span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ background: "color-mix(in srgb, var(--bt-accent) 8%, var(--bt-surface))" }}
        >
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${pct}%`, background: "var(--bt-accent)" }}
          />
        </div>
        <div className="grid grid-cols-3 gap-3 pt-2">
          <BackfillMetric testid="delta-connect-backfill-pages" label="Pages" value={pages} />
          <BackfillMetric testid="delta-connect-backfill-fills" label="Fills seen" value={Number(fills).toLocaleString()} />
          <BackfillMetric testid="delta-connect-backfill-trades" label="Trades paired" value={Number(trades).toLocaleString()} />
        </div>
      </div>

      {rateLimited && (
        <div
          data-testid="delta-connect-backfill-rate-limited"
          className="rounded-md border px-4 py-2.5 text-xs flex items-start gap-2"
          style={{
            borderColor: "color-mix(in srgb, var(--bt-warn) 40%, var(--bt-border))",
            background: "color-mix(in srgb, var(--bt-warn) 6%, var(--bt-surface))",
          }}
        >
          <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--bt-warn)" }} />
          <p>
            Delta briefly rate-limited us — we&apos;re paused and will resume automatically
            from where we left off within the next tick. Nothing you need to do.
          </p>
        </div>
      )}

      <div
        className="rounded-md border px-4 py-2.5 text-xs text-[var(--bt-text-muted)] flex items-start gap-2"
        style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface)" }}
      >
        <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--bt-accent)" }} />
        <p>
          If Delta briefly rate-limits us, we pause and resume from where we left off —
          you won&apos;t lose progress and you don&apos;t need to redo anything.
        </p>
      </div>
    </section>
  );
}

function BackfillMetric({ testid, label, value }) {
  return (
    <div className="rounded-md px-3 py-2" style={{ background: "var(--bt-surface)" }}>
      <p className="text-[10px] uppercase tracking-wider text-[var(--bt-text-muted)]">{label}</p>
      <p data-testid={testid} className="font-mono text-lg tabular-nums">{value}</p>
    </div>
  );
}

// ─── Screen 6 — Active (real disconnect wired) ────────────────────────

function ActiveCard({ conn, onDisconnected }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Iter 113 — fragmentation audit surfaced directly on the Active card
  // so the user sees "Delta split X of your Y orders …" without having
  // to curl the diagnostic endpoint manually. Rendered ONLY when the
  // backend actually found multi-fill orders (avoids noise for lucky
  // low-fragmentation accounts).
  const [frag, setFrag] = useState(null);
  // Iter 115 — one-shot force-reprocess. Used when the audit reports
  // stale trades (rebuild_version drift) or when the user just wants
  // to re-derive their journal from raw fills manually.
  const [reprocessing, setReprocessing] = useState(false);
  const refetchFrag = () => {
    api.get("/delta-connection/order-fragmentation-audit")
      .then(({ data }) => { if (data?.status === "ok") setFrag(data); })
      .catch(() => { /* silent — non-critical widget */ });
  };
  useEffect(() => {
    let alive = true;
    api.get("/delta-connection/order-fragmentation-audit")
      .then(({ data }) => { if (alive && data?.status === "ok") setFrag(data); })
      .catch(() => { /* silent — non-critical widget */ });
    return () => { alive = false; };
  }, [conn?.fills_fetched]);            // refetch when new fills land

  const lastSync = conn?.last_synced_at
    ? new Date(conn.last_synced_at).toLocaleString("en-IN",
        { hour: "numeric", minute: "2-digit", day: "numeric", month: "short" })
    : "just now";

  const copyMasked = async () => {
    try {
      await navigator.clipboard.writeText(conn?.api_key_masked || "");
      toast.success("Masked pass copied.");
    } catch { /* ignore */ }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/delta-disconnect");
      const kept = data?.changed?.trades_kept ?? 0;
      const unsup = data?.changed?.csv_unsuperseded ?? 0;
      toast.success(
        unsup > 0
          ? `Disconnected. ${kept} synced trades kept, ${unsup} CSV rows are visible again.`
          : `Disconnected. ${kept} synced trades kept in your journal.`
      );
      onDisconnected();
    } catch {
      toast.error("Couldn't disconnect — please try again.");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  // Iter 115 — force re-derive delta_api trades from raw fills using
  // the current rebuild logic. Idempotent, fills untouched. Used
  // primarily via the "Reprocess trades" affordance on the Active card;
  // becomes visually prominent when the audit endpoint reports stale
  // trades (rebuild_version drift after a deploy).
  const reprocessTrades = async () => {
    setReprocessing(true);
    try {
      const { data } = await api.post("/delta-connection/reprocess");
      if (data?.status === "locked") {
        toast.error(data.message || "A sync is running — try again in a minute.");
        return;
      }
      if (data?.status === "not_connected") {
        toast.error("Not connected. Reconnect first.");
        return;
      }
      const n = data?.trades_created ?? 0;
      toast.success(
        `Reprocessed — your journal now shows ${n} trade${n === 1 ? "" : "s"} from Delta.`
      );
      refetchFrag();
    } catch {
      toast.error("Couldn't reprocess right now — please try again.");
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <section
      data-testid="delta-connect-active-card"
      className="bt-card border rounded-sm p-6 space-y-5"
      style={{
        borderColor: "color-mix(in srgb, var(--bt-win) 40%, var(--bt-border))",
        background: "color-mix(in srgb, var(--bt-win) 5%, var(--bt-surface))",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "color-mix(in srgb, var(--bt-win) 18%, var(--bt-surface))" }}
        >
          <CheckCircle2 className="w-5 h-5" style={{ color: "var(--bt-win)" }} />
        </div>
        <div className="flex-1">
          <p className="bt-label" style={{ color: "var(--bt-win)" }}>Connected</p>
          <h2 className="font-heading text-xl">Delta auto-sync is on.</h2>
          <p className="text-sm text-[var(--bt-text-muted)]">
            New trades stream in live, with an automatic safety-net sync as backup. Last sync: <b>{lastSync}</b>.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <BackfillMetric testid="delta-connect-active-trades"
          label="Trades in journal"
          value={(conn?.backfill_stats?.trades_created ?? 0).toLocaleString()} />
        <BackfillMetric testid="delta-connect-active-fills"
          label="Fills pulled"
          value={(conn?.fills_fetched ?? 0).toLocaleString()} />
        <BackfillMetric testid="delta-connect-active-superseded"
          label="CSV rows deduped"
          value={(conn?.backfill_stats?.csv_superseded ?? 0).toLocaleString()} />
      </div>

      {frag && frag.orders_with_multiple_fills > 0 && (
        <FragmentationInsight frag={frag} />
      )}

      {frag && frag.stale && (
        <StaleTradesBanner onReprocess={reprocessTrades} busy={reprocessing} />
      )}

      <div
        data-testid="delta-connect-masked-row"
        className="rounded-md border px-4 py-3 flex items-center justify-between gap-3"
        style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface-2)" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Shield className="w-4 h-4 shrink-0" style={{ color: "var(--bt-text-muted)" }} />
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-[var(--bt-text-muted)]">Connected pass</p>
            <p data-testid="delta-connect-masked-key"
              className="font-mono text-sm truncate"
              title={conn?.api_key_masked}>
              {conn?.api_key_masked || "•••----"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={copyMasked}
          data-testid="delta-connect-copy-masked"
          className="p-2 rounded-md hover:bg-[var(--bt-surface)] transition-colors"
          aria-label="Copy masked pass"
          title="Copy masked pass"
        >
          <Copy className="w-4 h-4" style={{ color: "var(--bt-text-muted)" }} />
        </button>
      </div>

      {!confirming ? (
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setConfirming(true)}
              data-testid="delta-connect-disconnect"
              className="text-sm underline text-[var(--bt-text-muted)] hover:text-[var(--bt-loss)] self-start"
            >
              Disconnect this pass
            </button>
            <button
              type="button"
              onClick={reprocessTrades}
              disabled={reprocessing}
              data-testid="delta-connect-reprocess"
              className="text-sm underline text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] self-start disabled:opacity-50 disabled:cursor-not-allowed"
              title="Re-derive your journal from raw fills using the latest math"
            >
              {reprocessing ? "Reprocessing…" : "Reprocess trades"}
            </button>
          </div>
          <Link
            to="/trades?data_source=delta_api"
            data-testid="delta-connect-active-go-td"
            className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-black rounded-md px-4 py-2"
            style={{ background: "var(--bt-accent)" }}
          >
            Open my synced trades <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div
          data-testid="delta-connect-disconnect-confirm"
          className="rounded-md border px-4 py-3 space-y-3"
          style={{
            borderColor: "color-mix(in srgb, var(--bt-loss) 40%, var(--bt-border))",
            background: "color-mix(in srgb, var(--bt-loss) 5%, var(--bt-surface))",
          }}
        >
          <p className="text-sm">
            <b>Disconnect Delta sync?</b> Your synced trades stay in your journal.
            Any CSV rows we hid because they had an API twin will become visible again.
            You can reconnect anytime.
          </p>
          <div className="flex items-center gap-2">
            <Button
              onClick={disconnect}
              disabled={busy}
              data-testid="delta-connect-disconnect-confirm-btn"
              className="text-white font-semibold"
              style={{ background: "var(--bt-loss)" }}
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Yes, disconnect
            </Button>
            <Button
              variant="outline"
              onClick={() => setConfirming(false)}
              disabled={busy}
              data-testid="delta-connect-disconnect-cancel-btn"
              className="border-[var(--bt-border)]"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Screen 7 — Error (real reason from backend) ─────────────────────

function ErrorCard({ conn, onRetry }) {
  const cat = classifyReason(conn?.error_reason);
  const c = ERROR_COPY[cat] || ERROR_COPY.default;
  const isRevoked = cat === "revoked_or_rotated_key";
  const isIpRestriction = cat === "ip_not_whitelisted";
  const errRef = extractErrorRef(conn?.error_reason);
  const [copiedRef, setCopiedRef] = useState(false);

  return (
    <section
      data-testid="delta-connect-error-card"
      className="bt-card border rounded-sm p-6 space-y-4"
      style={{
        borderColor: "color-mix(in srgb, var(--bt-loss) 45%, var(--bt-border))",
        background: "color-mix(in srgb, var(--bt-loss) 5%, var(--bt-surface))",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "color-mix(in srgb, var(--bt-loss) 18%, var(--bt-surface))" }}
        >
          <AlertTriangle className="w-5 h-5" style={{ color: "var(--bt-loss)" }} />
        </div>
        <div className="flex-1">
          <p className="bt-label" style={{ color: "var(--bt-loss)" }}>Not synced</p>
          <h2 data-testid="delta-connect-error-headline" className="font-heading text-xl">{c.headline}</h2>
          <p data-testid="delta-connect-error-body" className="text-sm text-[var(--bt-text-muted)] mt-1 leading-relaxed">
            {c.body}
          </p>
        </div>
      </div>

      {isIpRestriction && conn?.delta_blocked_ip && (
        <div
          data-testid="delta-blocked-ip-callout"
          className="rounded-md border px-4 py-3 text-sm"
          style={{ borderColor: "color-mix(in srgb, var(--bt-warn) 55%, var(--bt-border))", background: "color-mix(in srgb, var(--bt-warn) 10%, transparent)" }}
        >
          <p className="font-semibold flex items-center gap-1.5" style={{ color: "var(--bt-warn)" }}>
            <AlertTriangle className="w-4 h-4" /> Delta blocked a request from{" "}
            <span className="font-mono">{conn.delta_blocked_ip}</span>
          </p>
          <p className="text-xs text-[var(--bt-text-muted)] mt-1">
            This is the exact IP Delta saw — it&apos;s <b>one of our egress IPs</b>, and the one
            your key is missing. Our servers rotate across several IPs, so <b>add ALL of them
            below</b> (use &quot;Copy all IPs&quot;) — whitelisting just one is not enough.
          </p>
        </div>
      )}
      {isIpRestriction && <EgressIpPanel variant="error" broker={DEFAULT_BROKER} />}

      {errRef && (
        <div
          data-testid="delta-connect-error-ref"
          className="rounded-md border px-4 py-3 text-xs flex items-center justify-between gap-2"
          style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface-2)" }}
        >
          <span className="text-[var(--bt-text-muted)]">
            Reference ID: <b className="font-mono text-[var(--bt-text)]">{errRef}</b>
            <span className="ml-1">— quote this to support@braintrades.ai and we can look up exactly what failed.</span>
          </span>
          <Button
            type="button" variant="outline" size="sm"
            onClick={async () => {
              try { await navigator.clipboard.writeText(errRef); setCopiedRef(true); setTimeout(() => setCopiedRef(false), 1800); } catch { /* noop */ }
            }}
            data-testid="delta-connect-error-ref-copy"
            className="border-[var(--bt-border)] shrink-0 h-7 px-2.5"
          >
            <Copy className="w-3.5 h-3.5 mr-1" /> {copiedRef ? "Copied" : "Copy"}
          </Button>
        </div>
      )}

      {isRevoked && conn?.api_key_masked && (
        <div
          className="rounded-md border px-4 py-3 text-xs text-[var(--bt-text-muted)]"
          style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface-2)" }}
        >
          Previously connected pass: <b className="font-mono text-[var(--bt-text)]">{conn.api_key_masked}</b>.
          Create a fresh one — we can&apos;t reuse this pass.
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
        <Link
          to="/trade-doctor"
          className="text-sm text-[var(--bt-text-muted)] hover:text-[var(--bt-text)]"
          data-testid="delta-connect-error-back"
        >
          Back to Trade Doctor
        </Link>
        <Button onClick={onRetry} data-testid="delta-connect-error-retry"
          className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-semibold">
          {c.action} <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </section>
  );
}


// ─── Stale trades banner (iter 115) ──────────────────────────────────
// Renders when /delta-connection/order-fragmentation-audit reports
// `stale: true`, i.e. the user's delta_api trades were derived by an
// older rebuild version. The startup sweeper reprocesses these
// automatically at server boot, but this banner gives the user a
// one-click affordance to force an immediate reprocess without waiting
// for the next sync tick or restart.
function StaleTradesBanner({ onReprocess, busy }) {
  return (
    <div
      data-testid="delta-connect-stale-banner"
      className="rounded-md border px-4 py-3 space-y-2"
      style={{
        borderColor: "color-mix(in srgb, var(--bt-loss) 40%, var(--bt-border))",
        background: "color-mix(in srgb, var(--bt-loss) 5%, var(--bt-surface-2))",
      }}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className="w-4 h-4 mt-0.5 shrink-0"
          style={{ color: "var(--bt-loss)" }}
        />
        <div className="flex-1 space-y-1.5">
          <p className="text-sm font-semibold" style={{ color: "var(--bt-loss)" }}>
            Your journal trades may be stale
          </p>
          <p className="text-xs text-[var(--bt-text-muted)] leading-relaxed">
            These trades were computed with an older version of our aggregation
            and fee math. Reprocess to re-derive them from raw fills using the
            current logic — your fills are untouched.
          </p>
          <button
            type="button"
            onClick={onReprocess}
            disabled={busy}
            data-testid="delta-connect-stale-reprocess-btn"
            className="text-xs font-semibold underline text-[var(--bt-text)] hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Reprocessing…" : "Reprocess trades now →"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Fragmentation insight (iter 113) ────────────────────────────────
// Renders on the Active card when the user's Delta history contains
// multi-fill orders. Turns the invisible iter112 aggregation fix into
// visible value: shows how many orders Delta sliced, how many phantom
// trades were prevented, and (optionally) the single worst-fragmented
// order as a concrete anchor.
function FragmentationInsight({ frag }) {
  const orders_multi = frag.orders_with_multiple_fills;
  const total_orders = frag.total_orders;
  const extras_absorbed = frag.extra_fills_absorbed_by_aggregation;
  const pct = total_orders > 0
    ? Math.round((orders_multi / total_orders) * 100)
    : 0;
  const worst = (frag.top_fragmented_orders || [])[0];

  return (
    <div
      data-testid="delta-connect-fragmentation-card"
      className="rounded-md border px-4 py-3 space-y-2"
      style={{
        borderColor: "color-mix(in srgb, var(--bt-accent) 40%, var(--bt-border))",
        background: "color-mix(in srgb, var(--bt-accent) 5%, var(--bt-surface-2))",
      }}
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--bt-accent)" }} />
        <div className="flex-1 space-y-1.5">
          <p
            className="bt-label"
            style={{ color: "var(--bt-accent)" }}
            data-testid="delta-connect-fragmentation-label"
          >
            Execution intelligence
          </p>
          <p className="text-sm leading-relaxed">
            Delta split{" "}
            <b data-testid="delta-connect-fragmentation-orders-multi">{orders_multi.toLocaleString()}</b>
            {" "}of your{" "}
            <b data-testid="delta-connect-fragmentation-total-orders">{total_orders.toLocaleString()}</b>
            {" "}orders (<b>{pct}%</b>) into multiple fills.
            {" "}
            {frag.stale
              ? "Your journal shows one row per fill — click Reprocess above to merge them into one row per order."
              : "We merged them so your journal shows one trade per order, not per-fill."}
          </p>
          {extras_absorbed > 0 && (
            <p
              className="text-xs text-[var(--bt-text-muted)]"
              data-testid="delta-connect-fragmentation-extras"
            >
              That prevented{" "}
              <b className="text-[var(--bt-text)]">{extras_absorbed.toLocaleString()}</b>{" "}
              phantom trades from cluttering your win-rate and average-trade-size stats.
            </p>
          )}
          {worst && worst.fill_count >= 3 && (
            <p
              className="text-xs text-[var(--bt-text-muted)]"
              data-testid="delta-connect-fragmentation-worst"
            >
              Most-fragmented order: <span className="font-mono">{worst.contract}</span>{" "}
              — <b>{worst.fill_count}</b> fills within one order.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
